import { Button } from '@/components/ui/button'
import { UserRole } from '@/domain/auth/auth'
import { useAppVersion } from '@/hooks/useAppVersion'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { Paths } from '@/routes/paths'
import { useAppStore } from '@/store/useAppStore'
import { useAuthStore } from '@/store/useAuthStore'
import { cn } from '@/utils/tailwind-util'
import { Link, useLocation } from '@tanstack/react-router'
import {
  Calculator,
  ChevronLeft,
  ChevronRight,
  Cog,
  FileText,
  LayoutDashboard,
  MapPin,
  Receipt,
  ShieldCheck,
  Trophy,
  UserCheck,
  UserKey,
  Users,
  UserStar,
  Wallet,
  BarChart2
} from 'lucide-react'

const navigation = [
  { name: 'Menú principal', href: Paths.Management.Dashboard, icon: LayoutDashboard },
  {
    name: 'Clientes', href: `${Paths.Management.Clients}`, icon: UserCheck,
    requiredRoles: [UserRole.Employee, UserRole.Secretary, UserRole.Admin, UserRole.SuperAdmin, UserRole.Scheduler]
  },
  {
    name: 'Contrataciones', href: `${Paths.Management.Contracts}`, icon: FileText,
    requiredRoles: [UserRole.Secretary, UserRole.Admin, UserRole.SuperAdmin, UserRole.Scheduler]
  },
  {
    name: 'Abonos', href: `${Paths.Management.Subscriptions}`, icon: ShieldCheck,
    requiredRoles: [UserRole.Secretary, UserRole.Admin, UserRole.SuperAdmin, UserRole.Scheduler]
  },
  {
    name: 'Planes de Pago', href: `${Paths.Management.PaymentPlans}`, icon: Calculator,
    requiredRoles: [UserRole.Secretary, UserRole.Admin, UserRole.SuperAdmin, UserRole.Scheduler]
  },
  {
    name: 'Cremaciones', href: `${Paths.Management.Cremations}`, icon: Trophy,
    requiredRoles: [UserRole.Secretary, UserRole.Admin, UserRole.SuperAdmin, UserRole.Scheduler]
  },
  {
    name: 'Parcelas', href: `${Paths.Management.Parcels}`, icon: MapPin,
    requiredRoles: [UserRole.Employee, UserRole.Secretary, UserRole.Admin, UserRole.SuperAdmin, UserRole.Scheduler]
  },
  {
    name: 'Caja', href: `${Paths.Management.CashBox}`, icon: Wallet,
    requiredRoles: [UserRole.Admin, UserRole.SuperAdmin, UserRole.Scheduler],
     children: [Paths.Management.Accounts, Paths.Management.AccountDetails]
  },
  {
    name: 'Facturación', href: Paths.Management.Invoicing, icon: Receipt,
    requiredRoles: [UserRole.Secretary, UserRole.Admin, UserRole.SuperAdmin, UserRole.Scheduler]
  },
  {
    name: 'Cobranza', href: `${Paths.Management.Collectors}`, icon: UserKey,
    requiredRoles: [UserRole.Admin, UserRole.SuperAdmin, UserRole.Scheduler]
  },
  {
    name: 'Ventas', href: `${Paths.Management.Sales}`, icon: UserStar,
    requiredRoles: [UserRole.Admin, UserRole.SuperAdmin, UserRole.Scheduler]
  },
  {
    name: 'Reportes', href: `${Paths.Management.Reports}`, icon: BarChart2,
    requiredRoles: [UserRole.SuperAdmin, UserRole.Scheduler]
  },
  {
    name: 'Empleados', href: `${Paths.Management.Users}`, icon: Users,
    requiredRoles: [UserRole.Admin, UserRole.SuperAdmin, UserRole.Scheduler]
  },
  {
    name: 'Servicios', href: `${Paths.Management.Offerings}`, icon: Cog,
    requiredRoles: [UserRole.Admin, UserRole.SuperAdmin, UserRole.Scheduler]
  },
]

export function ManagementSidebar() {
  const location = useLocation()
  const { sidebarOpen, toggleSidebar, mobileSidebarOpen, setMobileSidebarOpen } = useAppStore()
  const { user, hasAnyRole } = useAuthStore()
  const { versionData } = useAppVersion()
  const isDesktop = useMediaQuery('md')

  // Filter navigation items based on user roles
  const filteredNavigation = navigation.filter(item => {
    if (item.requiredRoles) {
      return hasAnyRole(item.requiredRoles)
    }
    return true
  })


  return (
    <>
      {/* Desktop Sidebar */}
      {isDesktop && (
        <div className={cn(
          "fixed left-0 top-0 z-40 h-screen bg-card border-r transition-all duration-300 flex flex-col overflow-hidden",
          sidebarOpen ? "w-50" : "w-16"
        )}>
          {/* Header */}
          <div className="flex h-16 items-center justify-between px-4 border-b">
            {sidebarOpen && (
              <img src="/logo.png" alt="Logo" className="w-24" />
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleSidebar}
              className="h-8 w-8"
            >
              {sidebarOpen ? (
                <ChevronLeft className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </Button>
          </div>

          {/* Navigation */}
          <nav className="p-4 space-y-2 pb-16 flex-1 overflow-y-auto">
            {filteredNavigation.map((item) => {
              const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/') || item.children?.some(child => location.pathname === child || location.pathname.startsWith(child + '/'))
              const Icon = item.icon

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                    isActive
                      ? "bg-blue-600 text-white"
                      : "text-muted-foreground hover:bg-blue-50 hover:text-blue-600",
                    !sidebarOpen && "justify-center"
                  )}
                >
                  <Icon className="h-5 w-5 flex-shrink-0" />
                  {sidebarOpen && <span>{item.name}</span>}
                </Link>
              )
            })}
          </nav>

          {sidebarOpen && (
            <div className="absolute bottom-0 w-full px-4 py-2 border-t bg-card text-center">
              <span className="text-xs text-muted-foreground">{versionData}</span>
            </div>
          )}
        </div>
      )}

      {/* Mobile Sidebar */}
      {!isDesktop && (
        <>
          {/* Mobile backdrop overlay */}
          {mobileSidebarOpen && (
            <div
              className="fixed inset-0 z-30 bg-black/50"
              onClick={() => setMobileSidebarOpen(false)}
            />
          )}

          <div className={cn(
            "fixed left-0 top-0 z-40 h-screen w-60 bg-card border-r transition-transform duration-300 flex flex-col overflow-hidden",
            mobileSidebarOpen ? "translate-x-0" : "-translate-x-full"
          )}>
            {/* Header */}
            <div className="flex h-16 items-center justify-between px-4 border-b">
              <img src="/logo.png" alt="Logo" className="w-24" />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileSidebarOpen(false)}
                className="h-8 w-8"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            </div>

            {/* Navigation */}
            <nav className="p-4 space-y-2 pb-16 overflow-y-auto">
              {filteredNavigation.map((item) => {
                const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/') || item.children?.some(child => location.pathname === child || location.pathname.startsWith(child + '/'))
                const Icon = item.icon

                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setMobileSidebarOpen(false)}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                      isActive
                        ? "bg-blue-600 text-white"
                        : "text-muted-foreground hover:bg-blue-50 hover:text-blue-600"
                    )}
                  >
                    <Icon className="h-5 w-5 flex-shrink-0" />
                    <span>{item.name}</span>
                  </Link>
                )
              })}
            </nav>

            <div className="px-4 py-2 border-t mt-auto text-center">
              <span className="text-xs text-muted-foreground">v0.0.2</span>
            </div>
          </div>
        </>
      )}
    </>
  )
}
